import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NETWORK_ERROR_STATUS,
  getRecognitionErrorCorrelationId,
  getRecognitionErrorMessage,
  getRecognitionErrorStatus,
  useEuropeUsaImport,
} from '@/api/recognition';
import type { EuropeUsaImportResultDto } from '@/api/types';
import { buildLocalizedErrorsCsv, localizeRowError } from './localizedErrors';

const ERRORS_PER_PAGE = 100;

interface Props {
  csv: string;
  ou: string;
  preview: EuropeUsaImportResultDto;
  importer: ReturnType<typeof useEuropeUsaImport>;
  onBack: () => void;
  onDone: () => void;
}

/** Paso 4 del wizard de Usuarios: recapitulación + confirmación EXPLÍCITA (nunca automática). */
export function StepConfirm({ csv, ou, preview, importer, onBack, onDone }: Readonly<Props>) {
  const { t } = useTranslation('recognition');
  const [page, setPage] = useState(1);

  const committed = importer.data && !importer.data.preview ? importer.data : null;

  const confirm = () => importer.mutate({ csv, preview: false, ou });

  // 🔴 Bug reportado 2026-09-14 (punto 3): esta pantalla solo mostraba contadores — un fallo real
  // al escribir (ej. correo ya usado por otra persona) queda registrado en `committed.errors` pero
  // nunca llegaba a verse aquí, así que "creados: 50" podía incluir filas que en realidad no se
  // guardaron. Mismo patrón de tabla + descarga que ya usa StepReview en la vista previa.
  const committedErrors = committed?.errors ?? [];
  const totalPages = Math.max(1, Math.ceil(committedErrors.length / ERRORS_PER_PAGE));
  const pageErrors = committedErrors.slice((page - 1) * ERRORS_PER_PAGE, page * ERRORS_PER_PAGE);

  const downloadErrors = () => {
    if (committedErrors.length === 0) return;
    const blob = new Blob(['﻿' + buildLocalizedErrorsCsv(committedErrors, t)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'errores-padron.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bulk-step bulk-step--confirm">
      {!committed && (
        <>
          <p className="bulk-intro">
            {t('admin.bulkUsers.confirmRecap', {
              total: preview.totalRows, created: preview.created,
              updated: preview.updated, deactivated: preview.deactivated,
            })}
          </p>

          {importer.isError && (
            <p className="rp-error" role="alert">
              <i className="fas fa-triangle-exclamation" />{' '}
              {getRecognitionErrorStatus(importer.error) === NETWORK_ERROR_STATUS
                // Nunca hubo respuesta HTTP: el backend puede haber terminado de escribir igual
                // (bug real 2026-09-04, ver client.ts NETWORK_ERROR_STATUS). El correlationId permite
                // cruzar contra el ImportAudit persistido en Mongo aunque los logs ya no existan.
                ? t('admin.bulkUsers.importErrorTimeout', {
                    correlationId: getRecognitionErrorCorrelationId(importer.error) ?? '—',
                  })
                : getRecognitionErrorMessage(importer.error, t('admin.bulkUsers.importError'))}
            </p>
          )}

          <div className="bulk-wizard-nav">
            <button type="button" className="btn btn--secondary" onClick={onBack} disabled={importer.isPending}>
              <i className="fas fa-arrow-left" /> {t('bulk.back')}
            </button>
            <button type="button" className="btn btn--send" onClick={confirm} disabled={importer.isPending}>
              <i className="fas fa-check" /> {t('admin.bulkUsers.confirm')}
            </button>
          </div>
        </>
      )}

      {committed && (
        <>
          <p className="adm-note adm-note--ok adm-note--strong">
            <i className="fas fa-circle-check" />{' '}
            {t('admin.bulkUsers.committed', {
              created: committed.created, updated: committed.updated,
              deactivated: committed.deactivated, skipped: committed.skipped,
            })}
          </p>

          {committedErrors.length > 0 && (
            <>
              <p className="adm-note adm-note--warn" role="alert">
                <i className="fas fa-triangle-exclamation" /> {t('admin.bulkUsers.committedErrorsNotice')}
              </p>
              <div className="adm-toolbar">
                <button type="button" className="btn btn--secondary" onClick={downloadErrors}>
                  <i className="fas fa-download" /> {t('admin.bulkUsers.downloadErrors')}
                </button>
              </div>
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>{t('bulk.colLine')}</th>
                      <th>{t('admin.bulkUsers.errorColumn')}</th>
                      <th>{t('admin.bulkUsers.errorDetail')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageErrors.map((rowError, index) => (
                      <tr key={`${rowError.lineNumber}-${rowError.column}-${index}`} className="adm-row--err">
                        <td>{rowError.lineNumber}</td>
                        <td>{rowError.column}</td>
                        <td>{localizeRowError(rowError, t)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="adm-review-pagination">
                  <button type="button" className="btn btn--secondary" disabled={page === 1}
                    onClick={() => setPage((current) => current - 1)}>
                    {t('bulk.back')}
                  </button>
                  <span>{t('admin.bulkUsers.paginationPage', { page, total: totalPages })}</span>
                  <button type="button" className="btn btn--secondary" disabled={page === totalPages}
                    onClick={() => setPage((current) => current + 1)}>
                    {t('bulk.next')}
                  </button>
                </div>
              )}
            </>
          )}

          <div className="bulk-wizard-nav">
            <span />
            <button type="button" className="btn btn--send" onClick={onDone}>{t('common.close')}</button>
          </div>
        </>
      )}
    </div>
  );
}

export default StepConfirm;
